/**
 * AuditIQ Sports + Health IQ — Command Center
 * One master dashboard. 11 sections. Role-gated. Mobile responsive.
 *
 * Visible to: super_admin · university_admin · athletic_director · head_coach ·
 *             trainer_health · academic_advisor · compliance_officer · sponsor_viewer
 * Each role sees only the sections their permission set unlocks.
 */
import React, { useEffect, useMemo, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Toaster } from "../../components/ui/sonner";
import { toast } from "sonner";
import { sportsApi } from "../../lib/sportsApi";
import { auth } from "../../lib/api";
import {
  Activity, AlertTriangle, Award, BarChart3, Briefcase, ClipboardList,
  Crown, Database, DollarSign, Download, Eye, FileText, Flame, Gauge,
  GraduationCap, Heart, Loader2, RefreshCw, ScanLine, ShieldCheck,
  Stethoscope, Target, TrendingUp, Trophy, Users2, ArrowRight,
} from "lucide-react";

const SECTION_ROLES = {
  exec:        ["super_admin","university_admin","athletic_director","head_coach","trainer_health","academic_advisor","compliance_officer","sponsor_viewer","read_only_auditor"],
  team:        ["super_admin","university_admin","athletic_director","head_coach","assistant_coach","position_coach","strength_coach","trainer_health"],
  health:      ["super_admin","university_admin","athletic_director","head_coach","trainer_health","strength_coach"],
  injury:      ["super_admin","university_admin","athletic_director","head_coach","trainer_health"],
  recruiting:  ["super_admin","university_admin","athletic_director","head_coach","recruiting_coordinator"],
  academic:    ["super_admin","university_admin","athletic_director","academic_advisor"],
  compliance:  ["super_admin","university_admin","athletic_director","compliance_officer","read_only_auditor"],
  performance: ["super_admin","university_admin","athletic_director","head_coach","strength_coach","trainer_health"],
  financial:   ["super_admin","university_admin","athletic_director"],
  data:        ["super_admin","university_admin","athletic_director","compliance_officer"],
  reports:     ["super_admin","university_admin","athletic_director","head_coach","compliance_officer","academic_advisor","read_only_auditor","trainer_health"],
};

// readiness pill
const READY_BG = {
  green:  ["#10B981","Ready"],
  yellow: ["#F5C84B","Monitor"],
  orange: ["#F59E0B","Limited Activity"],
  red:    ["#EF4444","Hold / Review"],
};
const ReadyDot = ({ k, label }) => {
  const [c, lbl] = READY_BG[k] || ["#94A3B8", k];
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
          style={{ background: `${c}1f`, color: c, border: `1px solid ${c}55` }}>
      <span className="w-2 h-2 rounded-full" style={{ background: c }} />{label || lbl}
    </span>
  );
};

const Stat = ({ label, value, sub, accent = "gold", testid, Icon }) => (
  <div className="card-premium glass" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{label}</div>
      {Icon && <Icon size={14} className={`text-${accent}`} />}
    </div>
    <div className={`font-display text-3xl mt-2 text-${accent}`}>{value}</div>
    {sub && <div className="text-cream-dim text-xs mt-2">{sub}</div>}
  </div>
);

const Bar = ({ label, value, max = 100, color = "var(--auditiq-electric)" }) => (
  <div className="mb-3 last:mb-0">
    <div className="flex justify-between text-xs text-cream-dim mb-1"><span>{label}</span><span className="text-cream">{value}{max === 100 ? "%" : ""}</span></div>
    <div className="h-2 rounded-full bg-navy-deep overflow-hidden border border-gold-dim/20">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} />
    </div>
  </div>
);

const SectionShell = ({ title, Icon, testid, children, action }) => (
  <section className="mt-10" data-testid={testid}>
    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-gold" />
        <h2 className="font-display">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const SportsCommandCenter = () => {
  const [user, setUser] = useState(null);
  const [unis, setUnis] = useState([]);
  const [universityId, setUniversityId] = useState("fsu");
  const [summary, setSummary] = useState(null);
  const [teamPerf, setTeamPerf] = useState([]);
  const [financial, setFinancial] = useState(null);
  const [load, setLoad] = useState(null);
  const [dataMon, setDataMon] = useState(null);
  const [matchQ, setMatchQ] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [generatingType, setGeneratingType] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth + uni list
  useEffect(() => {
    if (auth.token()) auth.me().then(setUser).catch(() => {});
    sportsApi.universities().then(d => setUnis(d.universities || []));
  }, []);
  // Role gate
  const role = user?.role || "";
  const can = (key) => SECTION_ROLES[key].includes(role);

  // Load all data when uni changes
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    const tasks = [
      sportsApi.commandSummary({ university_id: universityId }).then(r => setSummary(r)).catch(() => setSummary(null)),
      sportsApi.commandTeamPerf({ university_id: universityId }).then(r => setTeamPerf(r.teams || [])).catch(() => setTeamPerf([])),
      sportsApi.reports().then(r => setReportTypes(r.report_types || [])).catch(() => setReportTypes([])),
    ];
    if (can("financial")) tasks.push(sportsApi.commandFinancial({ university_id: universityId }).then(setFinancial).catch(() => setFinancial(null)));
    if (can("performance")) tasks.push(sportsApi.commandTrainingLoad({ university_id: universityId }).then(setLoad).catch(() => setLoad(null)));
    if (can("data")) tasks.push(sportsApi.commandDataMonitoring().then(setDataMon).catch(() => setDataMon(null)));
    if (can("data")) tasks.push(sportsApi.matchQueue().then(r => setMatchQ(r.queue || [])).catch(() => setMatchQ([])));
    if (can("compliance")) tasks.push(sportsApi.compliance().then(r => setCompliance(r.records || [])).catch(() => setCompliance([])));
    if (can("recruiting")) tasks.push(sportsApi.prospects({ university_id: universityId }).then(r => setProspects(r.prospects || [])).catch(() => setProspects([])));
    Promise.all(tasks).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, universityId]);

  // Report download — uses authed fetch
  const downloadReport = async (type, fmt) => {
    setGeneratingType(`${type}:${fmt}`);
    try {
      // 1. generate (creates the report record)
      const r = await sportsApi.generateReport({ report_type: type, university_id: universityId, sport: "football" });
      // 2. download fmt
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/sports/reports/${r.id}/download?fmt=${fmt}`;
      const token = localStorage.getItem("auditiq_token");
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj; a.download = `${type}.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(obj), 1000);
      toast.success(`${r.title} (${fmt.toUpperCase()})`);
    } catch (err) { toast.error(`Download failed`); }
    finally { setGeneratingType(null); }
  };

  const downloadReadiness = async () => {
    setGeneratingType("readiness:pdf");
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/sports/reports/production-readiness?university_id=${universityId}`;
      const token = localStorage.getItem("auditiq_token");
      const resp = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = obj; a.download = "auditiq-sports-readiness.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(obj), 1000);
      toast.success("Production-readiness PDF generated");
    } catch (err) { toast.error("Failed"); }
    finally { setGeneratingType(null); }
  };

  // sponsor: aggregate-only synthesized view
  const isSponsor = role === "sponsor_viewer";

  return (
    <PortalLayout title="Sports AuditIQ + Health IQ Command Center">
      <Toaster position="top-right" richColors theme="dark" />
      <div className="container-x section-y">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Crown size={22} className="text-gold" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-electric">AuditIQ Sports</div>
              <h1 className="font-display">Command Center</h1>
              <div className="text-cream-dim text-xs mt-1">Logged in as <span className="text-cream">{user?.name || "guest"}</span> · role <span className="text-electric">{role || "—"}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={universityId} onValueChange={setUniversityId}>
              <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream w-72" data-testid="cc-uni-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                {unis.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={downloadReadiness} className="btn-gold h-11" disabled={generatingType === "readiness:pdf"} data-testid="cc-readiness-btn">
              {generatingType === "readiness:pdf" ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
              Production-Readiness PDF
            </Button>
          </div>
        </div>

        {loading && <div className="card-premium glass mt-6 flex items-center gap-3 text-cream-dim">
          <Loader2 size={18} className="animate-spin" /> Loading command center…
        </div>}

        {!loading && summary && (
          // ============ 1. EXECUTIVE SUMMARY ============
          <SectionShell title="Executive Summary" Icon={Gauge} testid="cc-section-exec">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat testid="cc-stat-athletes"      label="Total athletes"          value={summary.totals.athletes}        Icon={Users2} />
              <Stat testid="cc-stat-teams"         label="Active teams"            value={summary.totals.teams}           Icon={Trophy}      accent="electric" />
              <Stat testid="cc-stat-injury"        label="Injury alerts"           value={summary.totals.injury_alerts}   Icon={AlertTriangle} />
              <Stat testid="cc-stat-eligible"      label="Eligible athletes"       value={summary.totals.eligible_athletes} Icon={GraduationCap} accent="electric" />
              <Stat testid="cc-stat-prospects"     label="Recruiting prospects"    value={summary.totals.prospects}       Icon={ScanLine} />
              <Stat testid="cc-stat-compl"         label="Compliance flags"        value={summary.totals.compliance_flags} Icon={ShieldCheck} accent="electric" />
              <Stat testid="cc-stat-hiq-score"     label="Overall Health IQ"       value={summary.health.average_score}  Icon={Heart} />
              <Stat testid="cc-stat-program-ready" label="Program readiness"       value={`${summary.health.program_readiness_score}%`} Icon={Activity} accent="electric" />
            </div>
            <div className="card-premium glass-accent mt-4 flex items-start gap-3" data-testid="cc-disclaimer">
              <ShieldCheck size={16} className="text-electric mt-1 shrink-0" />
              <div className="text-cream-dim text-xs">{summary.disclaimer}</div>
            </div>
          </SectionShell>
        )}

        {!loading && can("team") && teamPerf.length > 0 && (
          // ============ 2. TEAM PERFORMANCE ============
          <SectionShell title="Team Performance — by sport" Icon={Trophy} testid="cc-section-team">
            <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
              {teamPerf.map(t => (
                <article key={t.team_id} className="card-premium glass" data-testid={`cc-team-${t.team_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{t.sport}</div>
                      <h3 className="font-display mt-1">{t.name}</h3>
                      <div className="text-cream-dim text-xs mt-1">{t.head_coach}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Record</div>
                      <div className="font-display text-2xl text-cream mt-1">{t.record}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
                    <div className="glass p-3"><div className="text-steel uppercase tracking-[0.18em] text-[9px]">Practice readiness</div><div className="text-electric mt-1 font-mono text-base">{t.practice_readiness_pct}%</div></div>
                    <div className="glass p-3"><div className="text-steel uppercase tracking-[0.18em] text-[9px]">Injury risk</div><div className="text-[color:var(--auditiq-warning)] mt-1 font-mono text-base">{t.injury_risk_pct}%</div></div>
                    <div className="glass p-3"><div className="text-steel uppercase tracking-[0.18em] text-[9px]">Attendance</div><div className="text-cream mt-1 font-mono text-base">{t.attendance_pct}%</div></div>
                    <div className="glass p-3"><div className="text-steel uppercase tracking-[0.18em] text-[9px]">Avg GPA</div><div className="text-cream mt-1 font-mono text-base">{t.avg_gpa}</div></div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    {Object.entries(t.readiness_mix).map(([k, v]) => v > 0 && (
                      <ReadyDot key={k} k={k} label={`${k} ${v}`} />
                    ))}
                    {t.compliance_flags > 0 && <span className="text-xs px-2 py-1 rounded bg-[color:var(--auditiq-warning)]/15 text-[color:var(--auditiq-warning)]">⚐ {t.compliance_flags} compliance</span>}
                  </div>
                </article>
              ))}
            </div>
          </SectionShell>
        )}

        {!loading && can("health") && summary && (
          // ============ 3. HEALTH IQ MONITORING ============
          <SectionShell title="Health IQ Monitoring" Icon={Heart} testid="cc-section-health">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(summary.health.readiness_mix).map(([k, v]) => (
                <div key={k} className="card-premium glass text-center" data-testid={`cc-hiq-${k}`}>
                  <div className="w-10 h-10 rounded-full mx-auto" style={{ background: READY_BG[k][0] }} />
                  <div className="mt-3 font-display text-cream capitalize">{READY_BG[k][1]}</div>
                  <div className="font-display text-3xl text-gold mt-1">{v}</div>
                  <div className="text-cream-dim text-xs mt-1">athletes</div>
                </div>
              ))}
            </div>
            <div className="card-premium glass mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-3">Status legend</div>
              <div className="flex flex-wrap gap-2">
                <ReadyDot k="green" /><ReadyDot k="yellow" /><ReadyDot k="orange" /><ReadyDot k="red" />
              </div>
              <div className="card-premium glass-accent mt-4 flex items-start gap-3">
                <AlertTriangle size={14} className="text-electric mt-0.5 shrink-0" />
                <div className="text-cream-dim text-xs">{summary.disclaimer}</div>
              </div>
            </div>
          </SectionShell>
        )}

        {!loading && can("injury") && summary && (
          // ============ 4. INJURY & RISK MONITORING ============
          <SectionShell title="Injury & Risk Monitoring" Icon={Flame} testid="cc-section-injury">
            <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Stat label="Active alerts"       value={summary.totals.injury_alerts} Icon={AlertTriangle} />
              <Stat label="RTP pending"         value={summary.totals.rtp_pending}   Icon={RefreshCw} accent="electric" />
              <Stat label="High-risk athletes"  value={summary.health.readiness_mix.red + summary.health.readiness_mix.orange} Icon={Flame} />
              <Stat label="Training-load alerts" value={load?.high_load_athletes ?? 0} Icon={Activity} accent="electric" />
              <Stat label="Clearance pending"   value={summary.totals.injury_alerts} Icon={Stethoscope} />
            </div>
          </SectionShell>
        )}

        {!loading && can("recruiting") && (
          // ============ 5. RECRUITING INTELLIGENCE ============
          <SectionShell title="Recruiting Intelligence" Icon={ScanLine} testid="cc-section-recruiting">
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat label="In pipeline" value={prospects.length} Icon={Users2} />
              <Stat label="Offers"      value={prospects.filter(p => p.stage === "Offered").length} Icon={Award} accent="electric" />
              <Stat label="Visits"      value={prospects.filter(p => p.stage === "Visited").length} Icon={Eye} />
              <Stat label="Commitments" value={prospects.filter(p => p.stage === "Committed").length} Icon={Trophy} accent="electric" />
              <Stat label="Transfers"   value={prospects.filter(p => p.notes?.includes?.("transfer")).length || 0} Icon={RefreshCw} />
              <Stat label="Crawler updates" value={dataMon?.data_updates_today ?? 0} Icon={Database} accent="electric" />
            </div>
            {prospects.length > 0 && (
              <div className="card-premium glass mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-3">Top AI-fit prospects</div>
                <div className="space-y-2">
                  {[...prospects].sort((a, b) => (b.ai_fit_score || 0) - (a.ai_fit_score || 0)).slice(0, 5).map(p => (
                    <div key={p.id} className="flex items-center justify-between text-sm border-b border-gold-dim/10 pb-2">
                      <div className="text-cream">{p.first_name} {p.last_name} · <span className="text-cream-dim text-xs">{p.sport} {p.position}</span></div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-electric">AI-fit {p.ai_fit_score}</span>
                        <span className="text-gold">{p.stage}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionShell>
        )}

        {!loading && can("academic") && summary && (
          // ============ 6. ACADEMIC & ELIGIBILITY ============
          <SectionShell title="Academic & Eligibility" Icon={GraduationCap} testid="cc-section-academic">
            <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Stat label="Eligible" value={summary.totals.eligible_athletes} Icon={GraduationCap} accent="electric" />
              <Stat label="At risk"  value={summary.totals.athletes - summary.totals.eligible_athletes} Icon={AlertTriangle} />
              <Stat label="Avg GPA"  value={teamPerf.length ? (teamPerf.reduce((a, t) => a + t.avg_gpa, 0) / teamPerf.length).toFixed(2) : "—"} Icon={BarChart3} />
              <Stat label="Attendance" value={`${teamPerf.length ? Math.round(teamPerf.reduce((a, t) => a + t.attendance_pct, 0) / teamPerf.length) : 0}%`} Icon={Eye} accent="electric" />
              <Stat label="Eligibility alerts" value={summary.totals.compliance_flags} Icon={ClipboardList} />
            </div>
          </SectionShell>
        )}

        {!loading && can("compliance") && (
          // ============ 7. COMPLIANCE & GOVERNANCE ============
          <SectionShell title="Compliance & Governance" Icon={ShieldCheck} testid="cc-section-compliance">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Stat label="Open items"       value={compliance.filter(c => c.status === "Action Required").length} Icon={AlertTriangle} />
              <Stat label="Under review"     value={compliance.filter(c => c.status === "Under Review").length} Icon={Eye} accent="electric" />
              <Stat label="Compliant"        value={compliance.filter(c => c.status === "Compliant").length} Icon={ShieldCheck} />
              <Stat label="Audit readiness"  value={`${Math.max(20, 100 - compliance.filter(c => c.status === "Action Required").length * 4)}%`} Icon={ClipboardList} accent="electric" />
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-3">Recent items</div>
              <div className="space-y-2">
                {compliance.slice(0, 6).map(c => (
                  <div key={c.id} className="flex justify-between text-sm border-b border-gold-dim/10 pb-2 gap-3 flex-wrap">
                    <div className="text-cream"><span className="text-electric text-xs uppercase tracking-wider">{c.type}</span> · <span className="font-mono text-xs text-cream-dim">{c.athlete_id}</span></div>
                    <span className={`text-xs ${c.status === "Compliant" ? "text-[color:var(--auditiq-success)]" : c.status === "Action Required" ? "text-[color:var(--auditiq-critical)]" : "text-[color:var(--auditiq-warning)]"}`}>{c.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </SectionShell>
        )}

        {!loading && can("performance") && load && (
          // ============ 8. PERFORMANCE & TRAINING LOAD ============
          <SectionShell title="Performance & Training Load" Icon={TrendingUp} testid="cc-section-performance">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="card-premium glass">
                <Bar label="Average training load" value={load.average_training_load} />
                <Bar label="Recovery rate"          value={load.recovery_rate_pct}  color="var(--auditiq-success)" />
                <Bar label="Game load index"        value={load.game_load_index} color="#F59E0B" />
              </div>
              <Stat label="Practice hours / week" value={load.practice_hours_week} Icon={Activity} />
              <Stat label="High-load athletes" value={load.high_load_athletes} Icon={Flame} accent="electric" />
              <div className="card-premium glass lg:col-span-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Performance trend</div>
                <div className={`font-display text-2xl ${load.performance_trend === "improving" ? "text-[color:var(--auditiq-success)]" : load.performance_trend === "declining" ? "text-[color:var(--auditiq-critical)]" : "text-cream"}`}>
                  {load.performance_trend.charAt(0).toUpperCase() + load.performance_trend.slice(1)}
                </div>
              </div>
            </div>
          </SectionShell>
        )}

        {!loading && can("financial") && financial && (
          // ============ 9. FINANCIAL / PROGRAM OVERSIGHT ============
          <SectionShell title="Financial / Program Oversight" Icon={DollarSign} testid="cc-section-financial">
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <Stat label={`${financial.fiscal_year} Budget`} value={`$${(financial.budget_total_usd / 1_000_000).toFixed(1)}M`} Icon={DollarSign} />
              <Stat label="Utilization" value={`${financial.utilization_pct}%`} Icon={Gauge} accent="electric" />
              <Stat label="Program ROI index" value={financial.program_roi_index} Icon={Trophy} />
            </div>
            <div className="card-premium glass">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-3">Line items</div>
              <div className="space-y-2">
                {financial.lines.map(l => (
                  <div key={l.label} className="flex items-center justify-between text-sm border-b border-gold-dim/10 pb-2">
                    <span className="text-cream">{l.label}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-cream font-mono">${l.amount_usd.toLocaleString()}</span>
                      <span className="text-cream-dim">{l.trend}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionShell>
        )}

        {!loading && can("data") && dataMon && (
          // ============ 10. AI CRAWLING & DATA MONITORING ============
          <SectionShell title="AI Crawling & Data Monitoring" Icon={Database} testid="cc-section-data">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Stat label="Sources connected" value={`${dataMon.sources_connected} / ${dataMon.sources_total}`} Icon={Database} />
              <Stat label="Data updates today" value={dataMon.data_updates_today} Icon={RefreshCw} accent="electric" />
              <Stat label="Data accuracy" value={`${dataMon.data_accuracy_pct}%`} Icon={ShieldCheck} />
              <Stat label="Pending match reviews" value={dataMon.pending_match_reviews} Icon={Eye} />
              <Stat label="Crawl errors today" value={dataMon.crawl_errors_today} Icon={AlertTriangle} accent="electric" />
              <div className="card-premium glass">
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Last crawl</div>
                <div className="font-display text-sm text-cream mt-2">{dataMon.last_crawl_at ? new Date(dataMon.last_crawl_at).toLocaleString() : "—"}</div>
              </div>
            </div>
            {matchQ.length > 0 && (
              <div className="card-premium glass mt-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-3">Pending match approvals</div>
                <div className="space-y-2">
                  {matchQ.slice(0, 5).map(m => (
                    <div key={m.id} className="flex justify-between text-sm border-b border-gold-dim/10 pb-2 flex-wrap gap-2">
                      <span className="text-cream">{m.candidate_name}</span>
                      <span className="text-electric text-xs">{m.confidence}% · {m.recommended_action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionShell>
        )}

        {!loading && can("reports") && reportTypes.length > 0 && (
          // ============ 11. REPORTS ============
          <SectionShell title="Reports — PDF & CSV exports" Icon={FileText} testid="cc-section-reports">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reportTypes.map(rt => (
                <article key={rt.key} className="card-premium glass" data-testid={`cc-report-${rt.key}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-gold">{rt.audience}</div>
                  <div className="font-display text-cream mt-2">{rt.label}</div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={() => downloadReport(rt.key, "pdf")}
                            disabled={generatingType === `${rt.key}:pdf`}
                            className="btn-ghost-gold h-9 text-xs flex-1"
                            data-testid={`cc-pdf-${rt.key}`}>
                      {generatingType === `${rt.key}:pdf` ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />} PDF
                    </Button>
                    <Button onClick={() => downloadReport(rt.key, "csv")}
                            disabled={generatingType === `${rt.key}:csv`}
                            className="btn-ghost-gold h-9 text-xs flex-1"
                            data-testid={`cc-csv-${rt.key}`}>
                      {generatingType === `${rt.key}:csv` ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />} CSV
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          </SectionShell>
        )}

        {/* Sponsor view fallback */}
        {!loading && isSponsor && summary && (
          <SectionShell title="Sponsor-safe aggregate view" Icon={Award} testid="cc-section-sponsor">
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Athletes engaged"  value={summary.totals.athletes} />
              <Stat label="Active programs"   value={summary.totals.teams} accent="electric" />
              <Stat label="Universities"      value={unis.length} />
            </div>
            <div className="card-premium glass-accent mt-4">
              <div className="text-cream-dim text-sm">
                Sponsor view contains only aggregated, sponsor-safe metrics — no
                private athlete, medical, or academic information is exposed.
              </div>
            </div>
          </SectionShell>
        )}
      </div>
    </PortalLayout>
  );
};

export default SportsCommandCenter;
